import {Button, Menu, Tooltip} from '@mui/material'
import {makeStyles} from '@oc/styles'
import {observer} from 'mobx-react-lite'
import {IconByKey} from '@oc/icons'
import * as React from 'react'
import {DisplayLanguage} from './OcLanguagesListRowRenderer'
import {AdditionalVirtualListProps, OcLanguagesMenuItems} from './OcLanguagesMenuItems'

const useStyles = makeStyles({
    languagesButton: {
        textTransform: 'none'
    },
    list: {
        padding: 0
    }
})

export interface OcLanguagesSelectionProps {
    selectedLanguage: DisplayLanguage
    suggestions: DisplayLanguage[]
    onChangeHandler: (suggestion?: DisplayLanguage) => void
    onSearchHandler: (searchTerm: string) => void
    showFilter: boolean
    searchLabel: string
    width?: number
    maxHeight?: number
    rowHeight?: number
    additionalVirtualListProps?: AdditionalVirtualListProps
    showFullName?: boolean
}

function _OcLanguagesSelection({
    selectedLanguage,
    suggestions,
    onChangeHandler,
    onSearchHandler,
    showFilter,
    searchLabel,
    width = 250,
    maxHeight = 350,
    rowHeight = 40,
    additionalVirtualListProps,
    showFullName
}: OcLanguagesSelectionProps) {
    const {classes} = useStyles()
    const [anchor, setAnchor] = React.useState<HTMLElement | undefined>(undefined)

    const openMenu = React.useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget),
        []
    )
    const closeMenu = React.useCallback(() => setAnchor(undefined), [])
    const onChange = React.useCallback(
        (suggestion?: DisplayLanguage) => {
            onChangeHandler(suggestion)
            closeMenu()
        },
        [onChangeHandler, closeMenu]
    )

    return (
        <>
            <Tooltip title={selectedLanguage.fullName}>
                <Button
                    id="oc-languages-selection"
                    className={classes.languagesButton}
                    color="secondary"
                    onClick={openMenu}
                >
                    <IconByKey iconKey={'language'} size="24px" color="action" key={`language_iconSvg`} />
                    {showFullName ? selectedLanguage.fullName : selectedLanguage.nameWithoutRegion}
                </Button>
            </Tooltip>
            <Menu
                id="oc-languages-selection-menu"
                classes={{list: classes.list}}
                anchorEl={anchor}
                open={!!anchor}
                onClose={closeMenu}
            >
                <OcLanguagesMenuItems
                    selectedLanguage={selectedLanguage}
                    suggestions={suggestions}
                    onChangeHandler={onChange}
                    onSearchHandler={onSearchHandler}
                    showFilter={showFilter}
                    rowHeight={rowHeight}
                    width={width}
                    searchLabel={searchLabel}
                    height={Math.min(suggestions.length * rowHeight, maxHeight)}
                    additionalVirtualListProps={additionalVirtualListProps}
                />
            </Menu>
        </>
    )
}

export const OcLanguagesSelection = observer(_OcLanguagesSelection)
